// ─────────────────────────────────────────────────────────────────────────
// Hearth — combat condition metadata: severity coloring, attacker-advantage
// detection, and short SRD tooltips. Used by the CombatTracker to render
// prominent, meaningful condition chips without a network round-trip.
// ─────────────────────────────────────────────────────────────────────────

export type ConditionTone = "bad" | "warn" | "good";

interface ConditionInfo {
  tone: ConditionTone;
  // True if a creature with this condition grants ADVANTAGE to attackers.
  grantsAdvantage?: boolean;
  desc: string;
}

// Keyed by lower-case condition name. Covers the 15 SRD conditions plus the
// engine's self-buff/effect labels (blessed, raging, …).
const CONDITIONS: Record<string, ConditionInfo> = {
  blinded: { tone: "warn", grantsAdvantage: true, desc: "Can't see; auto-fails sight checks. Attacks vs it have advantage; its attacks have disadvantage." },
  charmed: { tone: "warn", desc: "Can't attack the charmer; the charmer has advantage on social checks against it." },
  deafened: { tone: "warn", desc: "Can't hear; auto-fails hearing checks." },
  frightened: { tone: "warn", desc: "Disadvantage on checks and attacks while the source is in sight; can't willingly move closer." },
  grappled: { tone: "warn", desc: "Speed is 0; ends if the grappler is incapacitated." },
  incapacitated: { tone: "bad", desc: "Can't take actions or reactions." },
  invisible: { tone: "good", desc: "Can't be seen without aid; attacks against it have disadvantage, its attacks have advantage." },
  paralyzed: { tone: "bad", grantsAdvantage: true, desc: "Incapacitated, can't move/speak. Attacks vs it have advantage; melee hits within 5 ft are crits; auto-fails STR/DEX saves." },
  petrified: { tone: "bad", grantsAdvantage: true, desc: "Turned to stone, incapacitated, unaware. Attacks vs it have advantage; auto-fails STR/DEX saves; resists all damage." },
  poisoned: { tone: "warn", desc: "Disadvantage on attack rolls and ability checks." },
  prone: { tone: "warn", grantsAdvantage: true, desc: "Melee attacks vs it have advantage (ranged have disadvantage); its attacks have disadvantage." },
  restrained: { tone: "warn", grantsAdvantage: true, desc: "Speed 0. Attacks vs it have advantage; its attacks have disadvantage; disadvantage on DEX saves." },
  stunned: { tone: "bad", grantsAdvantage: true, desc: "Incapacitated, can't move, can barely speak. Attacks vs it have advantage; auto-fails STR/DEX saves." },
  unconscious: { tone: "bad", grantsAdvantage: true, desc: "Incapacitated, prone, unaware. Attacks vs it have advantage; melee hits within 5 ft are crits; auto-fails STR/DEX saves." },
  exhaustion: { tone: "warn", desc: "Stacking penalties to checks, speed, attacks, and saves." },
  // engine effect / buff labels
  blessed: { tone: "good", desc: "Bless: +1d4 to attack rolls and saving throws." },
  raging: { tone: "good", desc: "Rage: bonus melee damage and resistance to bludgeoning/piercing/slashing." },
  rage: { tone: "good", desc: "Rage: bonus melee damage and resistance to bludgeoning/piercing/slashing." },
  dead: { tone: "bad", desc: "Dead." },
  concentrating: { tone: "good", desc: "Maintaining concentration on a spell." },
};

export function conditionTone(name: string): ConditionTone {
  return CONDITIONS[name.toLowerCase()]?.tone ?? "warn";
}

export function conditionTooltip(name: string): string {
  return CONDITIONS[name.toLowerCase()]?.desc ?? name;
}

/** True if this condition grants attackers advantage against the creature. */
export function grantsAttackerAdvantage(name: string): boolean {
  return CONDITIONS[name.toLowerCase()]?.grantsAdvantage ?? false;
}

/** True if any condition in the list grants attackers advantage. */
export function anyGrantsAdvantage(conditions: string[]): boolean {
  return conditions.some(grantsAttackerAdvantage);
}
